// src/components/modals/professional/ProfessionalDetailModal.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    Row,
    Col,
    Badge,
    Form,
    Image,
    Alert,
} from 'react-bootstrap';
import {
    FaTimes,
    FaMapMarkerAlt,
    FaClock,
    FaPhone,
    FaEnvelope,
    FaUser,
    FaCalendar,
    FaDownload,
    FaCheckCircle,
    FaBan,
    FaLanguage,
    FaBriefcase,
    FaIdCard,
    FaGavel,
    FaExclamationTriangle,
    FaFileAlt,
    FaMap,
    FaAddressBook,
    FaHashtag,
    FaFileContract,
    FaUserShield,
    FaInfoCircle,
    FaHistory,
    FaCheck,
    FaTimesCircle,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

// ── Icône PDF ──────────────────────────────────────────────────────────────
const PDF_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';

// Données des services avec icônes (fallback si selectedService correspond à une clé connue)
const servicesData = {
    "garde_enfant": {
        name: "Garde d'enfant",
        icon: "assets/images/icon/2.png",
        subServices: [
            "Temps plein Baby-sitting ponctuel",
            "Assistance maternelle"
        ]
    },
    "aide_menagere": {
        name: "Aide ménagère",
        icon: "assets/images/icon/1.png",
        subServices: [
            "Nettoyage maison et bureau",
            "Aide ménagère complète",
            "Nettoyage spécialisé"
        ]
    },
    "aide_personnes_agees": {
        name: "Aide aux personnes âgées",
        icon: "assets/images/icon/3.png",
        subServices: [
            "Accompagnement personnalisé",
            "Aide au quotidien"
        ]
    },
    "soutien_scolaire": {
        name: "Soutien scolaire",
        icon: "assets/images/icon/4.png",
        subServices: [
            "Cours d'anglais",
            "Cours de mathématiques",
            "Cours d'informatique"
        ]
    },
    "reparation_electromenager": {
        name: "Réparation Électroménager",
        icon: "assets/images/icon/5.png",
        subServices: [
            "Réparation lave-linge",
            "Dépannage réfrigérateur"
        ]
    },
    "cuisine_a_domicile": {
        name: "Cuisine à Domicile",
        icon: "assets/images/icon/6.png",
        subServices: [
            "Cours de cuisine",
            "Préparation de repas",
            "Événements culinaires"
        ]
    },
    "jardinage": {
        name: "Jardinage",
        icon: "assets/images/icon/7.png",
        subServices: [
            "Entretien espace vert",
            "Création de jardins",
            "Taille de haies"
        ]
    }
};

// ── Helpers dates ────────────────────────────────────────────────────────────
const parseBackendDate = (str) => {
    if (!str) return null;
    const [datePart] = str.split(' ');
    const parts = datePart.split('-');
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
};

const formatDate = (dateStr) => {
    const d = parseBackendDate(dateStr);
    if (!d) return null;
    return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const [datePart, timePart] = dateStr.split(' ');
    const d = parseBackendDate(datePart);
    if (!d) return null;
    const time = timePart || '00:00';
    return `${d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })} à ${time}`;
};

// ── Détecte le type de fichier à partir d'une URL ──
const getFileType = (url) => {
    if (!url) return 'unknown';
    const withoutQuery = url.split('?')[0];
    const extension = withoutQuery.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'unknown';
};

// ── Découpe une liste backend "a, b, c" → ['a','b','c'] ──
const splitBackendList = (str) =>
    str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];


// ── Composant de lecture PDF ──
const PdfReader = ({ url }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [pdfError, setPdfError] = useState(false);
    const [pdfErrorDetail, setPdfErrorDetail] = useState('');
    const [pdfLoading, setPdfLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [pageWidth, setPageWidth] = useState(window.innerWidth - 32);
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        setPdfLoading(true);
        setPdfError(false);
        setPdfErrorDetail('');
        setBlobUrl(null);
        setNumPages(0);

        const load = async () => {
            try {
                const response = await fetch(url, {
                    mode: 'cors',
                    credentials: 'omit',
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) setBlobUrl(objectUrl);
            } catch (e) {
                console.error('Erreur de lecture du document PDF (probable CORS MinIO ou réseau) :', e);
                if (!cancelled) {
                    setPdfError(true);
                    setPdfErrorDetail(e.message || 'Erreur réseau/CORS');
                    setPdfLoading(false);
                }
            }
        };
        if (url) load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth - 20;
                setPageWidth(Math.max(width, 320));
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        if (containerRef.current) {
            const resizeObserver = new ResizeObserver(() => updateWidth());
            resizeObserver.observe(containerRef.current);
            return () => {
                window.removeEventListener('resize', updateWidth);
                resizeObserver.disconnect();
            };
        }

        return () => window.removeEventListener('resize', updateWidth);
    }, [blobUrl]);

    if (pdfError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '3rem 2rem',
                minHeight: '300px'
            }}>
                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                <h6>Aperçu indisponible</h6>
                <p className="text-muted mb-0">Ce document PDF n'a pas pu être chargé pour lecture.</p>
                {pdfErrorDetail && (
                    <p className="text-muted small mt-2">({pdfErrorDetail})</p>
                )}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            style={{
                minHeight: '300px',
                overflow: 'auto',
                background: '#f1f3f5',
                padding: '20px 10px',
                width: '100%'
            }}
        >
            {(pdfLoading || !blobUrl) && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '3rem 0',
                    minHeight: '200px'
                }}>
                    <div className="spinner-border text-primary" role="status" />
                    <p className="text-muted mt-3">Chargement du document…</p>
                </div>
            )}
            {blobUrl && (
                <Document
                    file={blobUrl}
                    onLoadSuccess={({ numPages: n }) => {
                        setNumPages(n);
                        setPdfLoading(false);
                    }}
                    onLoadError={(e) => {
                        console.error('Erreur pdf.js :', e);
                        setPdfError(true);
                        setPdfErrorDetail(e?.message || '');
                        setPdfLoading(false);
                    }}
                    loading={null}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16,
                        width: '100%'
                    }}>
                        {Array.from({ length: numPages }, (_, i) => (
                            <div
                                key={i}
                                style={{
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    background: '#fff',
                                    width: '100%',
                                    maxWidth: '100%',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <Page
                                    pageNumber={i + 1}
                                    width={pageWidth > 0 ? pageWidth : 320}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                />
                            </div>
                        ))}
                    </div>
                </Document>
            )}
        </div>
    );
};

const ProfessionalDetailModal = ({ professional, show, onClose, onValidate, onReject }) => {
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [activeTab, setActiveTab] = useState('profile');

    // Aperçu image (lightbox, avec zoom)
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState([]);

    // Aperçu document PDF
    const [docModal, setDocModal] = useState({ open: false, url: '', label: '' });

    if (!professional || !show) return null;

    const user = professional.user || {};
    const identification = professional.identification || {};
    const hasIdentification = !!professional.identification;
    const criminalRecord = professional.criminalRecord || {};
    const hasCriminalRecord = !!professional.criminalRecord;

    const profilePhotoUrl = professional.profilePhotoUrlPresigned || professional.profilePhotoUrl || user.profilePictureUrl;
    const cvUrl = professional.cvUrlPresigned || professional.cvUrl;
    const locationPlanUrl = professional.locationPlanUrlPresigned || professional.locationPlanUrl;

    const getServiceLabel = (service) => {
        return servicesData[service]?.name || service;
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'ACCEPTED':
                return <Badge bg="success" className="status-badge">Accepté</Badge>;
            case 'PENDING':
                return <Badge bg="warning" className="status-badge">En attente</Badge>;
            case 'REJECTED':
                return <Badge bg="danger" className="status-badge">Rejeté</Badge>;
            default:
                return <Badge bg="secondary" className="status-badge">En attente</Badge>;
        }
    };

    const getAvailabilityBadge = (availabilityStatus, hasIdentification, identificationSubmitted) => {
        if (!hasIdentification || identificationSubmitted === false) {
            return <Badge bg="secondary" className="availability-badge">Identification non soumise</Badge>;
        }
        return availabilityStatus ?
            <Badge bg="primary" className="availability-badge">Disponible</Badge> :
            <Badge bg="secondary" className="availability-badge">Indisponible</Badge>;
    };

    const getEnabledBadge = (enabled) => {
        return enabled ?
            <Badge bg="success" className="status-badge">Compte actif</Badge> :
            <Badge bg="danger" className="status-badge">Compte inactif</Badge>;
    };

    // ── Aperçu : routage selon le type de fichier ──
    const handleOpenPreview = (url, type, label = '') => {
        if (!url) return;
        if (type === 'image') {
            setLightboxSlides([{ src: url }]);
            setLightboxOpen(true);
            return;
        }
        if (type === 'pdf') {
            setDocModal({ open: true, url, label });
        }
    };

    const handleCloseDocModal = () => setDocModal({ open: false, url: '', label: '' });
    const handleCloseLightbox = () => setLightboxOpen(false);

    const handleDownloadFile = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleValidate = async () => {
        const result = await Swal.fire({
            title: 'Confirmer la validation',
            html: `Êtes-vous sûr de vouloir <strong>valider</strong> le professionnel <strong>${user.firstName} ${user.lastName}</strong> ?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, valider',
            cancelButtonText: 'Annuler',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            onValidate(professional.id);
            onClose();
        }
    };

    const handleReject = () => {
        setShowRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            Swal.fire('Erreur', 'Veuillez saisir le motif du rejet.', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Confirmer le rejet',
            html: `Êtes-vous sûr de vouloir <strong>rejeter</strong> le professionnel <strong>${user.firstName} ${user.lastName}</strong> ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, rejeter',
            cancelButtonText: 'Annuler',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            onReject(professional.id, rejectReason);
            setShowRejectModal(false);
            setRejectReason('');
            onClose();
        }
    };

    const cancelReject = () => {
        setShowRejectModal(false);
        setRejectReason('');
    };

    // ── Affichage d'un fichier ──
    const FileDisplay = ({ url, label }) => {
        const fileType = getFileType(url);

        if (!url) {
            return (
                <div className="file-display-empty">
                    <FaIdCard size={24} className="text-muted" />
                    <span className="text-muted">Aucun fichier</span>
                </div>
            );
        }

        return (
            <div className="file-display" onClick={() => handleOpenPreview(url, fileType, label)}>
                <div className="file-preview">
                    {fileType === 'image' ? (
                        <img src={url} alt={label} className="file-image" />
                    ) : fileType === 'pdf' ? (
                        <div className="file-pdf">
                            <img src={PDF_ICON_URL} alt="PDF" className="pdf-icon-img" />
                            <span>PDF</span>
                        </div>
                    ) : (
                        <div className="file-unknown">
                            <FaIdCard size={32} />
                            <span>Document</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié" }) => (
        <div className="display-field">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value">
                {value || emptyText}
            </div>
        </div>
    );

    const TabButton = ({ tabKey, label }) => (
        <button
            className={`tab-button ${activeTab === tabKey ? 'active' : ''}`}
            onClick={() => setActiveTab(tabKey)}
        >
            {label}
        </button>
    );

    const isCriminalRecordExpired = (() => {
        const d = parseBackendDate(criminalRecord.expiryDate);
        return d ? d < new Date() : false;
    })();

    const isIdentificationExpired = (() => {
        const d = parseBackendDate(identification.expiryDate);
        return d ? d < new Date() : false;
    })();

    return (
        <>
            {/* Overlay et modal principale */}
            <div
                className="professional-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="professional-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    {user.firstName} {user.lastName}
                                </h4>
                                <div className="d-flex gap-2 align-items-center flex-wrap mt-1">
                                    <small className="text-muted">{getServiceLabel(professional.selectedService)}</small>
                                    {professional.useRef && (
                                        <Badge bg="light" text="dark" className="border">
                                            <FaHashtag className="me-1" size={10} />
                                            {professional.useRef}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Tabs personnalisés */}
                        <div className="custom-tabs mb-4" style={{ borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '0', flexWrap: 'wrap' }}>
                            <TabButton tabKey="profile" label="👤 Profil" />
                            <TabButton tabKey="professional" label="💼 Profession" />
                            <TabButton tabKey="location" label="📍 Localisation" />
                            <TabButton tabKey="contacts" label="📞 Contacts" />
                            <TabButton tabKey="identification" label="🆔 Identification" />
                        </div>

                        {/* ═══════════════ Onglet Profil ═══════════════ */}
                        {activeTab === 'profile' && (
                            <div className="tab-content">
                                <Row className="g-4">
                                    <Col xs={12} md={4}>
                                        <div className="text-center">
                                            <Image
                                                src={profilePhotoUrl}
                                                alt={`${user.firstName} ${user.lastName}`}
                                                roundedCircle
                                                fluid
                                                style={{
                                                    width: '150px',
                                                    height: '150px',
                                                    objectFit: 'cover',
                                                    border: '4px solid #e9ecef',
                                                    marginBottom: '20px',
                                                    cursor: profilePhotoUrl ? 'pointer' : 'default',
                                                }}
                                                onClick={() => profilePhotoUrl && handleOpenPreview(profilePhotoUrl, 'image', 'Photo de profil')}
                                            />
                                            {user.username && (
                                                <div className="text-muted small">
                                                    <FaUser className="me-1" />
                                                    @{user.username}
                                                </div>
                                            )}
                                        </div>
                                    </Col>

                                    <Col xs={12} md={8}>
                                        <div className="bg-light rounded-3 p-3">
                                            <h5 className="fw-bold mb-3 text-success">👤 Informations Personnelles</h5>
                                            <Row className="g-3">
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Prénom"
                                                        value={user.firstName}
                                                        icon={<FaUser />}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Nom"
                                                        value={user.lastName}
                                                        icon={<FaUser />}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Email"
                                                        value={user.email}
                                                        icon={<FaEnvelope />}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Téléphone"
                                                        value={user.phoneNumber}
                                                        icon={<FaPhone />}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Genre"
                                                        value={user.gender === 'MALE' ? 'Homme' : user.gender === 'FEMALE' ? 'Femme' : user.gender}
                                                        icon={<FaUser />}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Date de naissance"
                                                        value={formatDate(user.dateOfBirth)}
                                                        icon={<FaCalendar />}
                                                    />
                                                </Col>
                                                {user.additionalInfos && (
                                                    <Col xs={12}>
                                                        <DisplayField
                                                            label="Informations additionnelles"
                                                            value={user.additionalInfos}
                                                            icon={<FaInfoCircle />}
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>

                                        {/* Statut du compte */}
                                        <div className="bg-light rounded-3 p-3 mt-3">
                                            <h5 className="fw-bold mb-3 text-success">
                                                <FaUserShield className="me-2" />
                                                Référence
                                            </h5>

                                            <Row className="g-3">
                                                {user.useRef && (
                                                    <Col xs={12} sm={6}>
                                                        <DisplayField
                                                            label="Référence utilisateur"
                                                            value={user.useRef}
                                                            icon={<FaHashtag />}
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>

                                        {/* Audit */}
                                        {(user.createdAt || user.modifiedAt || user.createdBy || user.lastModifiedBy) && (
                                            <div className="bg-light rounded-3 p-3 mt-3">
                                                <h5 className="fw-bold mb-3 text-success">
                                                    <FaHistory className="me-2" />
                                                    Informations d'audit
                                                </h5>
                                                <Row className="g-3">
                                                    <Col xs={12} sm={6}>
                                                        <DisplayField
                                                            label="Créé le"
                                                            value={formatDateTime(user.createdAt)}
                                                            icon={<FaCalendar />}
                                                        />
                                                    </Col>
                                                    <Col xs={12} sm={6}>
                                                        <DisplayField
                                                            label="Créé par"
                                                            value={user.createdBy}
                                                            icon={<FaUser />}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* ═══════════════ Onglet Profession ═══════════════ */}
                        {activeTab === 'professional' && (
                            <div className="tab-content">
                                <div className="bg-light rounded-3 p-3">
                                    <h5 className="fw-bold mb-3 text-success">💼 Informations Professionnelles</h5>

                                    {/* Référence et statistiques */}
                                    <Row className="g-3 mb-4">
                                        {professional.useRef && (
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Référence professionnelle"
                                                    value={professional.useRef}
                                                    icon={<FaHashtag />}
                                                />
                                            </Col>
                                        )}
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Nombre total de contrats"
                                                value={professional.totalNumOfContracts !== undefined ? professional.totalNumOfContracts.toString() : '0'}
                                                icon={<FaFileContract />}
                                            />
                                        </Col>
                                    </Row>

                                    <div className="mb-4">
                                        <div className="display-field mb-3">
                                            <div className="field-label">
                                                <FaBriefcase className="me-2" />
                                                Service principal
                                            </div>
                                            <div className="field-value">
                                                {getServiceLabel(professional.selectedService) || "Aucun service sélectionné"}
                                            </div>
                                        </div>

                                        {professional.subServices && (
                                            <div className="mt-2">
                                                <div className="field-label mb-2">Sous-services</div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {splitBackendList(professional.subServices).map((subService, index) => (
                                                        <Badge key={index} bg="secondary" className="px-3 py-2">
                                                            {subService}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-4">
                                        <div className="field-label mb-2">
                                            <FaLanguage className="me-2" />
                                            Langues parlées
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {professional.languages ? splitBackendList(professional.languages).map((lang, index) => (
                                                <Badge key={index} bg="primary" className="px-3 py-2">
                                                    {lang}
                                                </Badge>
                                            )) : <span className="text-muted">Aucune langue spécifiée</span>}
                                        </div>
                                    </div>

                                    <Row className="g-3 mb-4">
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Années d'expérience"
                                                value={professional.yearsOfExperience ? `${professional.yearsOfExperience} ans` : "Non spécifié"}
                                                icon={<FaClock />}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Disponibilité"
                                                value={professional.availabilityStatus ? 'Disponible' : 'Indisponible'}
                                                icon={<FaCheckCircle />}
                                            />
                                        </Col>
                                        <Col xs={12}>
                                            <DisplayField
                                                label="À propos"
                                                value={professional.about}
                                                icon={<FaUser />}
                                            />
                                        </Col>
                                    </Row>

                                    {/* CV */}
                                    <hr className="my-4" />
                                    <h5 className="fw-bold mb-3 text-success">
                                        <FaFileAlt className="me-2" />
                                        Curriculum Vitae (CV)
                                    </h5>
                                    <div className="mb-3">
                                        <div className="field-label">Document CV</div>
                                        {cvUrl ? (
                                            <FileDisplay url={cvUrl} label="CV" />
                                        ) : (
                                            <Alert variant="info" className="mt-2">
                                                <FaFileAlt className="me-2" />
                                                Aucun CV n'a été fourni par ce professionnel.
                                            </Alert>
                                        )}
                                    </div>

                                    {/* Audit professionnel */}
                                    {(professional.modifiedAt || professional.lastModifiedBy) && (
                                        <>
                                            <hr className="my-4" />
                                            <h5 className="fw-bold mb-3 text-success">
                                                <FaHistory className="me-2" />
                                                Audit
                                            </h5>
                                            <Row className="g-3">
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Dernière modification"
                                                        value={formatDateTime(professional.modifiedAt)}
                                                        icon={<FaCalendar />}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <DisplayField
                                                        label="Modifié par"
                                                        value={professional.lastModifiedBy}
                                                        icon={<FaUser />}
                                                    />
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ═══════════════ Onglet Localisation ═══════════════ */}
                        {activeTab === 'location' && (
                            <div className="tab-content">
                                <div className="bg-light rounded-3 p-3">
                                    <h5 className="fw-bold mb-3 text-success">📍 Informations de Localisation</h5>
                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Ville (professionnel)"
                                                value={professional.city}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Adresse"
                                                value={professional.address}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Ville (utilisateur)"
                                                value={user.city}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Quartier"
                                                value={user.neighborhood}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Pays"
                                                value={user.country}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                    </Row>

                                    {/* Plan de localisation */}
                                    <hr className="my-4" />
                                    <h5 className="fw-bold mb-3 text-success">
                                        <FaMap className="me-2" />
                                        Plan de localisation
                                    </h5>
                                    <div className="mb-3">
                                        <div className="field-label">Plan de localisation du professionnel</div>
                                        {locationPlanUrl ? (
                                            <FileDisplay url={locationPlanUrl} label="Plan de localisation" />
                                        ) : (
                                            <Alert variant="info" className="mt-2">
                                                <FaMap className="me-2" />
                                                Aucun plan de localisation n'a été fourni.
                                            </Alert>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ═══════════════ Onglet Contacts ═══════════════ */}
                        {activeTab === 'contacts' && (
                            <div className="tab-content">
                                <div className="bg-light rounded-3 p-3">
                                    <h5 className="fw-bold mb-3 text-success">
                                        <FaAddressBook className="me-2" />
                                        Contacts d'urgence
                                    </h5>

                                    {professional.identificationSubmitted !== undefined && (
                                        <Alert
                                            variant={professional.identificationSubmitted ? 'success' : 'warning'}
                                            className="mb-4 d-flex align-items-center gap-2"
                                        >
                                            {professional.identificationSubmitted ?
                                                <FaCheckCircle size={18} /> :
                                                <FaExclamationTriangle size={18} />
                                            }
                                            <div>
                                                <strong>Identification soumise :</strong>{' '}
                                                {professional.identificationSubmitted ? 'Oui' : 'Non'}
                                            </div>
                                        </Alert>
                                    )}

                                    {/* Contact 1 */}
                                    <div className="border rounded-3 p-3 mb-3 bg-white">
                                        <h6 className="fw-bold mb-3 text-primary">
                                            <FaUser className="me-2" />
                                            Contact 1
                                        </h6>
                                        <Row className="g-3">
                                            <Col xs={12} sm={4}>
                                                <DisplayField
                                                    label="Nom"
                                                    value={professional.contact1Name}
                                                    icon={<FaUser />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={4}>
                                                <DisplayField
                                                    label="Téléphone"
                                                    value={professional.contact1Phone}
                                                    icon={<FaPhone />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={4}>
                                                <DisplayField
                                                    label="Relation"
                                                    value={professional.contact1Relationship}
                                                    icon={<FaUser />}
                                                />
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Contact 2 */}
                                    <div className="border rounded-3 p-3 bg-white">
                                        <h6 className="fw-bold mb-3 text-primary">
                                            <FaUser className="me-2" />
                                            Contact 2
                                        </h6>
                                        <Row className="g-3">
                                            <Col xs={12} sm={4}>
                                                <DisplayField
                                                    label="Nom"
                                                    value={professional.contact2Name}
                                                    icon={<FaUser />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={4}>
                                                <DisplayField
                                                    label="Téléphone"
                                                    value={professional.contact2Phone}
                                                    icon={<FaPhone />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={4}>
                                                <DisplayField
                                                    label="Relation"
                                                    value={professional.contact2Relationship}
                                                    icon={<FaUser />}
                                                />
                                            </Col>
                                        </Row>
                                    </div>

                                    {!professional.contact1Name && !professional.contact2Name && (
                                        <Alert variant="info" className="mt-3">
                                            <FaAddressBook className="me-2" />
                                            Aucun contact d'urgence n'a été fourni.
                                        </Alert>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ═══════════════ Onglet Identification ═══════════════ */}
                        {activeTab === 'identification' && (
                            <div className="tab-content">
                                <div className="bg-light rounded-3 p-3">
                                    <h5 className="fw-bold mb-3 text-success">🆔 Documents d'Identification</h5>

                                    {/* Motif de rejet */}
                                    {professional.status === 'REJECTED' && professional.rejectionReason && (
                                        <Alert variant="danger" className="d-flex align-items-start gap-2 mb-3">
                                            <FaExclamationTriangle size={18} className="mt-1 flex-shrink-0" />
                                            <div>
                                                <strong>Motif du rejet :</strong> {professional.rejectionReason}
                                            </div>
                                        </Alert>
                                    )}

                                    {/* Statut d'identification soumise */}
                                    {professional.identificationSubmitted !== undefined && (
                                        <Alert
                                            variant={professional.identificationSubmitted ? 'success' : 'warning'}
                                            className="d-flex align-items-center gap-2 mb-3"
                                        >
                                            {professional.identificationSubmitted ?
                                                <FaCheckCircle size={18} /> :
                                                <FaExclamationTriangle size={18} />
                                            }
                                            <div>
                                                <strong>Identification soumise :</strong>{' '}
                                                {professional.identificationSubmitted ? 'Oui' : 'Non'}
                                            </div>
                                        </Alert>
                                    )}

                                    {/* Détails du document d'identification */}
                                    <Row className="g-3 mb-3">
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Type de document"
                                                value={identification.documentType}
                                                icon={<FaIdCard />}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Nom du document"
                                                value={identification.documentName}
                                                icon={<FaFileAlt />}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Autorité d'émission"
                                                value={identification.issuingAuthority}
                                                icon={<FaGavel />}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Pays d'émission"
                                                value={identification.country}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Date d'émission"
                                                value={formatDate(identification.issueDate)}
                                                icon={<FaCalendar />}
                                                emptyText="Non spécifiée"
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <DisplayField
                                                label="Date d'expiration"
                                                value={formatDate(identification.expiryDate)}
                                                icon={<FaCalendar />}
                                                emptyText="Non spécifiée"
                                            />
                                            {isIdentificationExpired && (
                                                <div className="mt-2">
                                                    <Badge bg="danger">Document expiré</Badge>
                                                </div>
                                            )}
                                        </Col>
                                        {identification.enabled !== undefined && (
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Statut du document"
                                                    value={identification.enabled ? 'Activé' : 'Désactivé'}
                                                    icon={identification.enabled ?
                                                        <FaCheck className="text-success" /> :
                                                        <FaTimesCircle className="text-danger" />
                                                    }
                                                />
                                            </Col>
                                        )}
                                    </Row>

                                    <Row className="mt-3">
                                        <Col xs={12} md={4}>
                                            <div className="mb-3">
                                                <div className="field-label">Photo de profil</div>
                                                <FileDisplay url={profilePhotoUrl} label="Photo de profil" />
                                            </div>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <div className="mb-3">
                                                <div className="field-label">Document recto</div>
                                                <FileDisplay
                                                    url={identification.documentFrontUrlPresigned || identification.documentFrontUrl}
                                                    label="Recto"
                                                />
                                            </div>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <div className="mb-3">
                                                <div className="field-label d-flex align-items-center gap-2">
                                                    Document verso
                                                    <Badge bg="light" text="dark" className="border">optionnel</Badge>
                                                </div>
                                                {(identification.documentBackUrlPresigned || identification.documentBackUrl) ? (
                                                    <FileDisplay
                                                        url={identification.documentBackUrlPresigned || identification.documentBackUrl}
                                                        label="Verso"
                                                    />
                                                ) : (
                                                    <div className="file-display-empty">
                                                        <span className="text-muted">Non fourni</span>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    {/* Casier judiciaire */}
                                    <h5 className="fw-bold mb-3 text-success">
                                        <FaGavel className="me-2" />
                                        Casier Judiciaire
                                    </h5>

                                    {hasCriminalRecord && (
                                        <Row className="g-3 mb-3">
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Nom du document"
                                                    value={criminalRecord.documentName}
                                                    icon={<FaFileAlt />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Autorité d'émission"
                                                    value={criminalRecord.issuingAuthority}
                                                    icon={<FaGavel />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Pays d'émission"
                                                    value={criminalRecord.country}
                                                    icon={<FaMapMarkerAlt />}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Date d'émission"
                                                    value={formatDate(criminalRecord.issueDate)}
                                                    icon={<FaCalendar />}
                                                    emptyText="Non spécifiée"
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={4}>
                                                <DisplayField
                                                    label="Date d'expiration"
                                                    value={formatDate(criminalRecord.expiryDate)}
                                                    icon={<FaCalendar />}
                                                    emptyText="Non spécifiée"
                                                />
                                                {isCriminalRecordExpired && (
                                                    <div className="mt-2">
                                                        <Badge bg="danger">Document expiré</Badge>
                                                    </div>
                                                )}
                                            </Col>
                                            {criminalRecord.enabled !== undefined && (
                                                <Col xs={12} sm={6} md={4}>
                                                    <DisplayField
                                                        label="Statut du document"
                                                        value={criminalRecord.enabled ? 'Activé' : 'Désactivé'}
                                                        icon={criminalRecord.enabled ?
                                                            <FaCheck className="text-success" /> :
                                                            <FaTimesCircle className="text-danger" />
                                                        }
                                                    />
                                                </Col>
                                            )}
                                        </Row>
                                    )}

                                    <div className="mb-3">
                                        <div className="field-label">Extrait de casier judiciaire</div>
                                        <FileDisplay
                                            url={criminalRecord.documentFrontUrlPresigned || criminalRecord.documentFrontUrl}
                                            label="Casier judiciaire"
                                        />
                                    </div>

                                    {!hasCriminalRecord && (
                                        <Alert variant="info" className="mt-3">
                                            <FaGavel className="me-2" />
                                            Aucun casier judiciaire n'a été fourni par ce professionnel.
                                        </Alert>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer avec actions */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px', marginTop: 'auto'
                    }}>
                        <div className="d-flex justify-content-between w-100 align-items-center flex-wrap gap-2">
                            <div>
                                <small className="text-muted"></small>
                            </div>
                            <div className="d-flex gap-2">
                                {(professional.status === 'PENDING' || professional.status === 'REJECTED' || professional.status === '') && (
                                    <>
                                        <Button
                                            variant="danger"
                                            onClick={handleReject}
                                            className="rounded-pill d-flex align-items-center gap-2"
                                        >
                                            <FaBan /> Rejeter
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handleValidate}
                                            className="rounded-pill d-flex align-items-center gap-2"
                                        >
                                            <FaCheckCircle /> Valider
                                        </Button>
                                    </>
                                )}
                                <Button variant="secondary" onClick={onClose} className="rounded-pill d-flex align-items-center gap-2">
                                    <FaTimes /> Fermer
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de rejet */}
            {showRejectModal && (
                <div className="reject-modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1060, display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                }} onClick={cancelReject}>
                    <div className="reject-modal-container" style={{
                        backgroundColor: 'white', borderRadius: '24px',
                        width: '90%', maxWidth: '500px', animation: 'slideUp 0.3s ease'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div className="p-4">
                            <h5 className="fw-bold mb-3">Motif du rejet</h5>
                            <Form.Group>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Saisissez le motif du rejet..."
                                    className="mb-3"
                                />
                            </Form.Group>
                            <div className="d-flex gap-2 justify-content-end">
                                <Button variant="secondary" onClick={cancelReject} className="rounded-pill">
                                    Annuler
                                </Button>
                                <Button variant="danger" onClick={confirmReject} className="rounded-pill">
                                    Confirmer le rejet
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox pour zoomer les images */}
            <Lightbox
                open={lightboxOpen}
                close={handleCloseLightbox}
                slides={lightboxSlides}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3 }}
                controller={{ closeOnBackdropClick: true }}
            />

            {/* Modal de lecture des documents PDF */}
            {docModal.open && (
                <div className="preview-modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    zIndex: 1070,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center'
                }} onClick={handleCloseDocModal}>
                    <div className="preview-modal-container" style={{
                        backgroundColor: 'white',
                        width: '100%',
                        maxWidth: '100%',
                        height: '92vh',
                        maxHeight: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        animation: 'slideUp 0.3s ease'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 border-bottom d-flex justify-content-between align-items-center" style={{
                            backgroundColor: 'white',
                            flexShrink: 0
                        }}>
                            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 22, height: 22 }} />
                                {docModal.label || 'Document PDF'}
                            </h6>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleDownloadFile(docModal.url, (docModal.label || 'document') + '.pdf')}
                                    className="rounded-pill"
                                >
                                    <FaDownload className="me-1" /> Télécharger
                                </Button>
                                <button className="btn-close" onClick={handleCloseDocModal}></button>
                            </div>
                        </div>

                        <div style={{
                            overflow: 'auto',
                            flex: 1,
                            backgroundColor: '#f1f3f5',
                            padding: '0'
                        }}>
                            <PdfReader url={docModal.url} />
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .professional-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.25rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                .tab-button {
                    background: none;
                    border: none;
                    padding: 10px 16px;
                    font-size: 0.9rem;
                    font-weight: 500;
                    color: #6c757d;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border-bottom: 2px solid transparent;
                }

                .tab-button:hover {
                    color: #28a745;
                }

                .tab-button.active {
                    color: #28a745;
                    border-bottom-color: #28a745;
                }

                .file-display {
                    cursor: pointer;
                }

                .file-image {
                    width: 100px;
                    height: 100px;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    transition: transform 0.2s ease;
                }

                .file-image:hover {
                    transform: scale(1.05);
                }

                .file-pdf {
                    width: 100px;
                    height: 100px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .pdf-icon-img {
                    width: 50px;
                    height: 50px;
                }

                .file-display-empty {
                    width: 100px;
                    height: 100px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px dashed #dee2e6;
                }
            `}</style>
        </>
    );
};

export default ProfessionalDetailModal;